import api from '@services/api';
import type { ContestCatalogEntry } from '../types/contestCatalog';

const normalizeTitle = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

function catalogIdForTaskTitle(
  taskTitle: string,
  catalogContests: ContestCatalogEntry[]
): string | null {
  const normalizedTask = normalizeTitle(taskTitle);
  const exact = catalogContests.find((c) => normalizeTitle(c.title) === normalizedTask);
  if (exact) return exact.id;
  const partial = catalogContests.find(
    (c) =>
      normalizedTask.includes(normalizeTitle(c.title)) ||
      normalizeTitle(c.title).includes(normalizedTask)
  );
  return partial?.id ?? null;
}

function matchesAgeCategory(subCategory: string | null | undefined, ageCategory?: string): boolean {
  if (!ageCategory) return true;
  const want = normalizeTitle(ageCategory);
  const got = normalizeTitle(String(subCategory || ''));
  if (!got) return true;
  return got === want || got.includes(want) || want.includes(got);
}

export function toContestDateInputValue(iso: string | null | undefined): string {
  if (!iso) return '';
  const raw = String(iso).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw.slice(0, 10);
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function formatContestAdminScore(score: number | string | null | undefined, status?: string): string {
  if (status === 'rejected') return '';
  if (score === null || score === undefined || String(score).trim() === '') return '';
  return String(score);
}

export const MAX_CONTEST_VIDEOS_PER_DATE = 2;

export const CONTEST_TWO_PER_DAY_MESSAGE =
  'At most 2 contest videos can be recorded on the same day considering child development';

export function todayIsoDate(): string {
  const parsed = new Date();
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function formatContestSubmittedAt(iso: string | null | undefined): string {
  if (!iso) return '';
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return String(iso);
  return parsed.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function parseSelfAttemptCounts(
  attempts: unknown,
  successes: unknown,
): { ok: true; attempts: number; successes: number } | { ok: false; message: string } {
  const attemptsText = attempts === undefined || attempts === null ? '' : String(attempts).trim();
  const successText = successes === undefined || successes === null ? '' : String(successes).trim();
  if (!attemptsText || !successText) {
    return { ok: false, message: 'Enter total attempts and successes for this skill.' };
  }
  if (!/^\d+$/.test(attemptsText) || !/^\d+$/.test(successText)) {
    return { ok: false, message: 'Attempts and successes must be whole numbers of zero or more.' };
  }
  const nextAttempts = parseInt(attemptsText, 10);
  const nextSuccesses = parseInt(successText, 10);
  if (nextSuccesses > nextAttempts) {
    return { ok: false, message: 'Successes cannot exceed total attempts.' };
  }
  return { ok: true, attempts: nextAttempts, successes: nextSuccesses };
}

export function formatSelfAttemptCounts(
  attempts?: number | string | null,
  successes?: number | string | null,
): string {
  if (attempts === null || attempts === undefined || String(attempts).trim() === '') return '';
  if (successes === null || successes === undefined || String(successes).trim() === '') return `${attempts} att`;
  return `${successes} / ${attempts}`;
}

export function countContestVideosOnDate(
  rows: Record<string, PlayerContestSubmissionRow>,
  date: string,
  exceptCatalogId?: string,
): number {
  const day = String(date || '').trim();
  if (!day) return 0;
  return Object.values(rows || {}).filter((row) => {
    if (!row?.submitted) return false;
    if (row.status === 'rejected') return false;
    if (exceptCatalogId && row.catalogId === exceptCatalogId) return false;
    return String(row.recordedDate || '').trim() === day;
  }).length;
}

export interface PlayerContestSubmissionRow {
  catalogId: string;
  submitted: boolean;
  status: string;
  score: number | string | null;
  rating: number | null;
  feedback: string;
  videoUrl: string | null;
  originalFileName: string | null;
  recordedDate: string | null;
  createdAt: string | null;
  reviewedAt: string | null;
  contestCategory: string | null;
  selfAttempts: number | null;
  selfSuccesses: number | null;
}

type ApiSubmission = {
  status?: string;
  score?: number | string | null;
  rating?: number | null;
  feedback?: string | null;
  videoUrl?: string | null;
  originalFileName?: string | null;
  recordedDate?: string | null;
  createdAt?: string | null;
  reviewedAt?: string | null;
  contestCategory?: string | null;
  selfAttempts?: number | string | null;
  selfSuccesses?: number | string | null;
};

export function resolveContestVideoSrc(url: string | null | undefined): string {
  const value = String(url || '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('blob:')) return value;
  return value.startsWith('/') ? value : `/${value}`;
}

/**
 * Latest contest video per catalog skill for this player (and optional age).
 * Score is the admin value after validation — not self-evaluation.
 */
export async function fetchPlayerContestSubmissionsByCatalog(
  sport: string,
  playerId: string,
  catalogContests: ContestCatalogEntry[],
  ageCategory?: string,
): Promise<Record<string, PlayerContestSubmissionRow>> {
  if (!playerId || catalogContests.length === 0) return {};

  try {
    const tasksRes = await api.get(`/contest-tasks?sport=${encodeURIComponent(sport)}`);
    const tasks: { id: string; title: string }[] = tasksRes.data?.data || [];
    const byCatalog: Record<string, PlayerContestSubmissionRow> = {};

    await Promise.all(
      tasks.map(async (task) => {
        try {
          const subsRes = await api.get(
            `/contest-tasks/${task.id}/submissions?sport=${encodeURIComponent(sport)}&playerId=${encodeURIComponent(playerId)}`
          );
          const subs: ApiSubmission[] = subsRes.data?.data || [];
          const catalogId = catalogIdForTaskTitle(task.title, catalogContests);
          if (!catalogId) return;
          const matching = subs.filter((sub) => matchesAgeCategory(sub.contestCategory, ageCategory));
          const latest = matching[0];
          if (!latest) return;
          byCatalog[catalogId] = {
            catalogId,
            submitted: true,
            status: String(latest.status || 'submitted'),
            score: latest.score ?? null,
            rating: latest.rating ?? null,
            feedback: String(latest.feedback || '').trim(),
            videoUrl: latest.videoUrl ? String(latest.videoUrl) : null,
            originalFileName: latest.originalFileName ? String(latest.originalFileName) : null,
            recordedDate: toContestDateInputValue(latest.recordedDate) || null,
            createdAt: latest.createdAt || null,
            reviewedAt: latest.reviewedAt || null,
            contestCategory: latest.contestCategory || null,
            selfAttempts:
              latest.selfAttempts === null || latest.selfAttempts === undefined
                ? null
                : Number(latest.selfAttempts),
            selfSuccesses:
              latest.selfSuccesses === null || latest.selfSuccesses === undefined
                ? null
                : Number(latest.selfSuccesses),
          };
        } catch {
          // ignore per-task fetch errors
        }
      })
    );

    return byCatalog;
  } catch {
    return {};
  }
}

/**
 * Contest entry status from live API — only after login + video upload.
 * Public catalog must not use localStorage preview ticks.
 */
export async function fetchPlayerSubmittedCatalogIds(
  sport: string,
  playerId: string,
  catalogContests: ContestCatalogEntry[]
): Promise<string[]> {
  const byCatalog = await fetchPlayerContestSubmissionsByCatalog(sport, playerId, catalogContests);
  return Object.keys(byCatalog).filter((id) => byCatalog[id]?.submitted);
}

function contestNotesKey(sport: string, ageCategory: string, playerId?: string | null): string {
  return `encycgames.contestNotes.v1:${sport}:${ageCategory}:${playerId || 'anon'}`;
}

export function loadContestPlayerNotes(
  sport: string,
  ageCategory: string,
  playerId?: string | null,
): Record<string, string> {
  try {
    const raw = localStorage.getItem(contestNotesKey(sport, ageCategory, playerId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return Object.fromEntries(
      Object.entries(parsed).map(([id, value]) => [id, String(value || '')]),
    );
  } catch {
    return {};
  }
}

export function saveContestPlayerNotes(
  sport: string,
  ageCategory: string,
  playerId: string | null | undefined,
  notes: Record<string, string>,
): void {
  try {
    localStorage.setItem(contestNotesKey(sport, ageCategory, playerId), JSON.stringify(notes));
  } catch {
    /* ignore */
  }
}
